import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ServiceGalleryDialog } from "@/components/ServiceGalleryDialog";
import { Hammer, Wrench, Armchair, Leaf, Recycle, Shield, ArrowRight, MapPin, ChevronLeft, ChevronRight, X, MessageCircle, Star, Quote } from "lucide-react";
import axios from "axios";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";
import { usePublicData } from "@/hooks/usePublicData";
import { getImageUrl, SITE_ORIGIN, DEFAULT_PHONE } from "@/lib/publicSite";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";
import { MapContainer, TileLayer, Circle, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const API = `${window.location.origin}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Abergavenny coordinates and 20 mile radius
const ABERGAVENNY_CENTER = [51.8242, -3.0167]; // Abergavenny, Wales
const RADIUS_MILES = 20;
const RADIUS_METERS = RADIUS_MILES * 1609.34; // Convert miles to meters

// Static fallbacks in case the CMS endpoint is unreachable
const defaultServices = [
  {
    icon: Hammer,
    title: "Carpentry",
    description: "Custom woodwork, repairs, and installations using sustainably sourced materials.",
    image: "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/cwictpbb_IMG_3307.jpeg",
    category: "carpentry",
    slug: "carpentry"
  },
  {
    icon: Wrench,
    title: "General Repairs",
    description: "Home maintenance and repair services with eco-friendly practices.",
    image: "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/qcywp0ow_IMG_3303.jpeg",
    category: "general-repairs",
    slug: "general-repairs"
  },
  {
    icon: Armchair,
    title: "Furniture Builds",
    description: "Bespoke furniture crafted from reclaimed and sustainable wood.",
    image: "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/25f9bfvg_IMG_3732.jpeg",
    category: "furniture-builds",
    slug: "furniture-builds"
  }
];

// Map backend icon names → lucide components for CMS-driven services
const ICON_MAP = { Hammer, Wrench, Armchair };

// Default portfolio images (shown when no featured photos from customers)
const defaultPortfolioImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/25f9bfvg_IMG_3732.jpeg",
    alt: "Custom oak coffee table with matching stool",
    title: "Bespoke Coffee Table",
    category: "furniture-builds"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/7ed2srqr_IMG_3541.jpeg",
    alt: "Handcrafted wooden stools",
    title: "Handmade Stools",
    category: "furniture-builds"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/t2n8zufa_IMG_3309.jpeg",
    alt: "Custom built-in shelving with rattan cabinet",
    title: "Built-in Shelving",
    category: "carpentry"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/cwictpbb_IMG_3307.jpeg",
    alt: "Alcove storage with floating shelves",
    title: "Alcove Storage",
    category: "carpentry"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/qcywp0ow_IMG_3303.jpeg",
    alt: "Custom hallway shelving installation",
    title: "Hallway Shelving",
    category: "carpentry"
  }
];

// Fallback label for photo categories that don't match a service
const prettifyCategory = (slug) =>
  slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const features = [
  {
    icon: Leaf,
    title: "Eco-Friendly Materials",
    description: "We prioritize sustainable and recycled materials in every project."
  },
  {
    icon: Recycle,
    title: "Waste Reduction",
    description: "Minimal waste approach with proper recycling and disposal."
  },
  {
    icon: Shield,
    title: "Quality Guaranteed",
    description: "Expert craftsmanship backed by our satisfaction guarantee."
  }
];

const testimonials = [
  {
    name: "Susan Holder",
    text: "I would wholeheartedly recommend Sandy - he's reliable, trustworthy, knows his stuff inside out, and does a beautiful, painstaking job. He sourced reclaimed scaffolding, sanded and oiled them, and they look a treat. No trouble was too much.",
    service: "Shelving Installation"
  },
  {
    name: "Anna Walker",
    text: "I would highly recommend Sandy! He has been extremely creative, helpful and reliable. He and James were fantastic and did a brilliant job. What really tops it off is that they are also just very lovely genuine guys who wanted to ensure they did the best job for us.",
    service: "Multiple Home Repairs"
  },
  {
    name: "Jonathan Rajalingam",
    text: "Sandy managed to squeeze us in at pretty short notice. He was on time and completed the job cleanly and quickly at a very fair price. His work was meticulous and we are delighted with the end product. Will definitely be using Sustainable Handyman Services again.",
    service: "Blinds Fitting"
  },
  {
    name: "Tom Constable",
    text: "We've used Sandy on numerous projects. Ranging from general handyman activities through to superb reclaimed tables and benches. I can't recommend him highly enough. Knowledgeable, relaxed, friendly and passionate about doing a good job.",
    service: "Furniture & Handyman"
  },
  {
    name: "Debbie Jones",
    text: "We had Mark over for a full day doing lots of odd jobs after our extension. He worked really hard and completed tiling, door hooks, coat rack, mirrors, magnet boards and more. We've booked him again for more jobs. We are very happy!",
    service: "General Repairs"
  }
];

export default function LandingPage() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [portfolioImages, setPortfolioImages] = useState(defaultPortfolioImages);
  const [portfolioFilter, setPortfolioFilter] = useState('all');
  const [featuredReviews, setFeaturedReviews] = useState([]);

  // Shared cached public data (CMS blocks, business info, services) — no content flash
  const { bizInfo, services: publicServices, cms, ready } = usePublicData();

  useSeoHeadTags({
    title: "Sustainable Handyman Services | Abergavenny, Carpentry, Repairs & Furniture",
    description: `Professional handyman in Abergavenny, Monmouthshire. Expert carpentry, general repairs, furniture builds, fencing, garden buildings. Eco-friendly service. Free quotes - Call ${bizInfo?.business_phone || DEFAULT_PHONE}`,
    canonical: `${SITE_ORIGIN}/`,
    ogType: "website",
    // index.html ships the authoritative HomeAndConstructionBusiness JSON-LD —
    // duplicating it here would create conflicting entities.
  });

  const services = useMemo(() => {
    const items = publicServices || [];
    if (!items.length) return defaultServices;
    return items.map((s) => ({
      icon: ICON_MAP[s.icon] || Hammer,
      title: s.title,
      description: s.description,
      image: s.image_url,
      category: s.category,
      photos: s.photos || [],
      slug: s.slug,
    }));
  }, [publicServices]);

  // Track page view on load
  useEffect(() => {
    const trackPageView = async () => {
      try {
        // Get or create session ID
        let sessionId = sessionStorage.getItem('shs_session_id');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('shs_session_id', sessionId);
        }
        
        await axios.post(`${API}/analytics/pageview`, null, {
          params: {
            page: '/',
            referrer: document.referrer || null,
            session_id: sessionId
          }
        });
      } catch (error) {
        if (process.env.NODE_ENV === "development") console.warn('Analytics tracking failed:', error.message);
      }
    };
    trackPageView();
  }, []);

  // Fetch featured reviews
  useEffect(() => {
    const fetchFeaturedReviews = async () => {
      try {
        const response = await axios.get(`${API}/feedback/featured`);
        setFeaturedReviews(response.data || []);
      } catch (error) {
      }
    };
    fetchFeaturedReviews();
  }, []);

  // Fetch featured photos from API + published gallery items from CMS
  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const [portRes, galRes] = await Promise.allSettled([
          axios.get(`${API}/portfolio`),
          axios.get(`${API}/gallery`),
        ]);
        const featuredPhotos = (portRes.status === "fulfilled" ? (portRes.value.data.photos || []) : []);

        // Expand gallery items into individual photo cards so they render in the same grid
        const galleryPhotos = [];
        if (galRes.status === "fulfilled") {
          for (const item of galRes.value.data.items || []) {
            for (const url of item.photos || []) {
              galleryPhotos.push({
                src: url.startsWith("http") ? url : `${window.location.origin}${url}`,
                alt: item.title,
                title: item.title,
                category: (item.category || "").replace(/_/g, "-"),
                description: item.description,
              });
            }
          }
        }

        const combined = [...galleryPhotos, ...featuredPhotos];
        if (combined.length > 0) {
          setPortfolioImages([...combined, ...defaultPortfolioImages]);
        }
      } catch (error) {
        // silent — leave defaults
      }
    };
    fetchPortfolio();
  }, []);

  // Filter portfolio images based on selected category
  // Filter chips derived from published services (+ any photo categories not covered)
  const portfolioCategories = useMemo(() => {
    const cats = [{ value: "all", label: "All Projects" }];
    const seen = new Set(["all"]);
    const add = (value, label) => {
      const v = (value || "").trim().replace(/_/g, "-");
      if (!v || seen.has(v)) return;
      seen.add(v);
      cats.push({ value: v, label });
    };
    services.forEach((s) => add(s.category, s.title));
    portfolioImages.forEach((img) => {
      const v = (img.category || "").trim().replace(/_/g, "-");
      if (v) add(v, prettifyCategory(v));
    });
    return cats;
  }, [services, portfolioImages]);

  const filteredPortfolioImages = portfolioFilter === 'all' 
    ? portfolioImages 
    : portfolioImages.filter(img => ((img.category || 'general').replace(/_/g, '-')) === portfolioFilter);

  const openLightbox = (index) => {
    // Find the actual index in the filtered array
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % filteredPortfolioImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + filteredPortfolioImages.length) % filteredPortfolioImages.length);
  };

  // Handle service card click - scroll to portfolio and filter
  const [serviceGallery, setServiceGallery] = useState(null);

  const handleServiceClick = (category) => {
    setPortfolioFilter(category);
    // Scroll to portfolio section
    setTimeout(() => {
      document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNav />

      {/* Hero Section */}
      <section className="pt-20 pb-12 sm:pt-24 sm:pb-16 md:pt-32 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-4 sm:space-y-6" style={{ opacity: ready ? 1 : 0, transition: "opacity 0.4s ease" }}>
              <p className="text-accent font-medium tracking-wide uppercase text-xs sm:text-sm">Eco-Friendly Craftsmanship</p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-heading" data-testid="hero-headline">
                {cms("landing.hero_headline", "Sustainable Craftsmanship, Timeless Quality")}
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg" data-testid="hero-subheadline">
                {cms("landing.hero_subheadline", "Expert handyman services with a commitment to sustainability. From carpentry to furniture builds, we bring quality craftsmanship with eco-friendly practices.")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4">
                <Link to="/enquiry" data-testid="hero-enquiry-btn" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-medium">
                    Request a Quote
                    <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </Button>
                </Link>
                <a href="#services" data-testid="hero-services-btn" className="w-full sm:w-auto">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-medium">
                    Our Services
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative mt-6 lg:mt-0">
              <div className="aspect-square rounded-2xl sm:rounded-3xl overflow-hidden bg-secondary">
                <img 
                  src="https://images.pexels.com/photos/35578185/pexels-photo-35578185.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Sustainable craftsmanship" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-card p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg border border-border">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-accent/10 p-1.5 sm:p-2 rounded-full">
                    <Leaf className="h-5 w-5 sm:h-6 sm:w-6 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-sm sm:text-base">100% Sustainable</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Eco-friendly practices</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 sm:py-16 md:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-accent font-medium tracking-wide uppercase text-xs sm:text-sm mb-2">What We Offer</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight font-heading">Our Services</h2>
            <p className="text-muted-foreground mt-2 sm:mt-3 text-sm sm:text-base">Click a service to see our work</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {services.map((service, index) => (
              <Card 
                key={service.title || index} 
                className="bg-card border border-border/50 overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer group"
                data-testid={`service-card-${index}`}
                onClick={() => (service.photos?.length ? setServiceGallery(service) : handleServiceClick(service.category))}
              >
                <div className="aspect-video overflow-hidden relative">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300 flex items-center justify-center">
                    <span className="text-white bg-primary/80 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {service.photos?.length ? "View Photos →" : "View Projects →"}
                    </span>
                  </div>
                </div>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="bg-primary/10 p-1.5 sm:p-2 rounded-lg">
                      <service.icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold font-heading">{service.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm sm:text-base prose prose-sm max-w-none" dangerouslySetInnerHTML={{__html: service.description || ""}} />
                  {service.slug && (
                    <Link
                      to={`/services/${service.slug}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-primary text-sm font-medium mt-3 hover:underline"
                      data-testid={`service-learn-more-${service.slug}`}
                    >
                      Learn more <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <ServiceGalleryDialog
        service={serviceGallery}
        onClose={() => setServiceGallery(null)}
        onViewProjects={() => {
          const cat = serviceGallery?.category;
          setServiceGallery(null);
          handleServiceClick(cat);
        }}
      />

      {/* Portfolio Gallery Section */}
      <section id="portfolio" className="py-12 sm:py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-accent font-medium tracking-wide uppercase text-xs sm:text-sm mb-2">Our Work</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight font-heading">Recent Projects</h2>
            <p className="text-muted-foreground mt-2 sm:mt-3 max-w-2xl mx-auto text-sm sm:text-base">Take a look at some of our recent handcrafted pieces and installations</p>
          </div>
          
          {/* Category Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-6 sm:mb-8" data-testid="portfolio-filters">
            {portfolioCategories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setPortfolioFilter(cat.value)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  portfolioFilter === cat.value
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
                data-testid={`filter-${cat.value}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
            {filteredPortfolioImages.map((image, index) => (
              <div 
                key={image.url || image.id || index}
                className="group relative aspect-square rounded-lg sm:rounded-xl overflow-hidden cursor-pointer"
                data-testid={`portfolio-image-${index}`}
                onClick={() => openLightbox(index)}
              >
                <img 
                  src={getImageUrl(image.src)}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-medium text-sm">{image.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* No results message */}
          {filteredPortfolioImages.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No projects found in this category yet.</p>
              <button 
                onClick={() => setPortfolioFilter('all')}
                className="mt-4 text-primary hover:underline"
              >
                View all projects
              </button>
            </div>
          )}

          {/* Lightbox Dialog */}
          <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
            <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-0 bg-black/95 border-none overflow-hidden" data-testid="lightbox-dialog">
              <button 
                onClick={() => setLightboxOpen(false)}
                className="absolute top-4 right-4 z-50 text-white/70 hover:text-white transition-colors"
                data-testid="lightbox-close"
              >
                <X className="h-8 w-8" />
              </button>
              
              <div className="relative flex flex-col items-center justify-center min-h-[50vh] max-h-[90vh] overflow-auto p-4">
                {/* Previous Button */}
                <button 
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 hover:bg-white/20 p-3 rounded-full text-white transition-colors"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>

                {/* Image Container - scrollable */}
                <div className="flex items-center justify-center px-16 py-8 overflow-auto">
                  <img 
                    src={getImageUrl(filteredPortfolioImages[currentImageIndex]?.src)}
                    alt={filteredPortfolioImages[currentImageIndex]?.alt}
                    className="max-w-full max-h-[75vh] object-contain rounded-lg"
                    style={{ minHeight: '200px' }}
                  />
                </div>

                {/* Next Button */}
                <button 
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 hover:bg-white/20 p-3 rounded-full text-white transition-colors"
                  data-testid="lightbox-next"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>

                {/* Caption - fixed at bottom */}
                <div className="mt-4 text-center pb-2">
                  <p className="text-white font-medium text-lg">{filteredPortfolioImages[currentImageIndex]?.title}</p>
                  <p className="text-white/60 text-sm mt-1">{currentImageIndex + 1} / {filteredPortfolioImages.length}</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-primary"
          style={{
            backgroundImage: `url(https://images.pexels.com/photos/35583883/pexels-photo-35583883.jpeg?auto=compress&cs=tinysrgb&w=1200)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-primary/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-accent font-medium tracking-wide uppercase text-sm mb-2">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-heading text-white">Committed to Sustainability</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title || index} 
                className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20"
                data-testid={`feature-card-${index}`}
              >
                <div className="bg-accent p-3 rounded-lg w-fit mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold font-heading text-white mb-2">{feature.title}</h3>
                <p className="text-white/80">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-accent font-medium tracking-wide uppercase text-sm mb-2">What Our Customers Say</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-heading">Customer Reviews</h2>
            <div className="flex items-center justify-center gap-1 mt-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-accent text-accent" />
              ))}
              <span className="ml-2 text-muted-foreground">5.0 on Facebook</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Show featured reviews from API first, then fall back to default testimonials */}
            {(featuredReviews.length > 0 
              ? featuredReviews.map((review) => ({
                  name: review.customer_name,
                  text: review.comments,
                  service: review.service_type?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                  rating: review.overall_rating
                }))
              : testimonials.map(t => ({ ...t, rating: 5 }))
            ).slice(0, 6).map((testimonial, index) => (
              <Card 
                key={testimonial.id || testimonial.customer_name || index}
                className="bg-card border border-border/50 hover:-translate-y-1 transition-transform duration-300"
                data-testid={`testimonial-${index}`}
              >
                <CardContent className="p-6">
                  <Quote className="h-8 w-8 text-accent/30 mb-4" />
                  <p className="text-foreground mb-4 leading-relaxed">&ldquo;{testimonial.text}&rdquo;</p>
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div>
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.service}</p>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`h-4 w-4 ${i < (testimonial.rating || 5) ? 'fill-accent text-accent' : 'text-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <Link 
              to="/reviews"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium"
              data-testid="view-all-reviews"
            >
              View all reviews
              <ArrowRight className="h-4 w-4" />
            </Link>
            <span className="hidden sm:block text-muted-foreground">|</span>
            <Link 
              to="/feedback"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/90 font-medium transition-colors"
              data-testid="leave-review-btn"
            >
              <MessageCircle className="h-4 w-4" />
              Leave a Review
            </Link>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section id="area" className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-accent font-medium tracking-wide uppercase text-sm mb-2">Service Area</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-heading mb-4">20 Miles Around Abergavenny</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Based near Abergavenny, we proudly serve customers within a 20-mile radius. Our team travels throughout South Wales and the Welsh borders to bring quality handyman services to your doorstep.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full"><MapPin className="h-4 w-4 text-primary" /></div>
                  <span>Abergavenny & Monmouth</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full"><MapPin className="h-4 w-4 text-primary" /></div>
                  <span>Usk & Raglan</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full"><MapPin className="h-4 w-4 text-primary" /></div>
                  <span>Chepstow & Ross-on-Wye</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full"><MapPin className="h-4 w-4 text-primary" /></div>
                  <span>Newport & Cwmbran</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full"><MapPin className="h-4 w-4 text-primary" /></div>
                  <span>Hereford & Brecon</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-6">
                Not sure if we cover your area? Get in touch and we will let you know!
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border border-border h-[400px]">
              <MapContainer 
                center={ABERGAVENNY_CENTER} 
                zoom={9} 
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Circle 
                  center={ABERGAVENNY_CENTER}
                  radius={RADIUS_METERS}
                  pathOptions={{ 
                    color: '#5d7c3f', 
                    fillColor: '#5d7c3f', 
                    fillOpacity: 0.15,
                    weight: 2
                  }}
                />
                <Marker position={ABERGAVENNY_CENTER}>
                  <Popup>
                    <strong>Sustainable Handyman Services</strong><br />
                    Based near Abergavenny<br />
                    20-mile service radius
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-secondary rounded-3xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-heading mb-4">
              Ready to Fix Your Home?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Get in touch today for a free consultation. Let's create something sustainable together.
            </p>
            <Link to="/enquiry" data-testid="cta-enquiry-btn">
              <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg font-medium">
                Get a Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter bizInfo={bizInfo} services={publicServices} />
    </div>
  );
}
